import { useMemo } from 'react';
import { mainMenuItems, orgMigrationMenuItem } from './utils/utils';
import { appRoutes } from '../../../constants/appRoutes';
import { MainMenuItem } from './utils/types';
import { useSelector } from 'react-redux';
import { selectIsDisableAi } from '../../../reducers/userInfoReducer';
import { selectDefaultCreationEnvironment } from '../../pages/environments/environmentsReducer';
import {
  NON_SALESFORCE_ALLOWED_ROUTES,
  NON_SALESFORCE_ALLOWED_MENU_IDS,
} from '../../../constants/nonSalesforceConstants';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

const filterMenuColumnsForNonSalesforce = (item: MainMenuItem): MainMenuItem => {
  if (!item.menuColumns) return item;

  const filteredColumns = item.menuColumns
    .map((column) => ({
      ...column,
      menuItems: column.menuItems.filter((menuItem) =>
        NON_SALESFORCE_ALLOWED_ROUTES.includes(menuItem.route),
      ),
    }))
    .filter((column) => column.menuItems.length > 0);

  if (filteredColumns.length === 0) {
    return { ...item, isHidden: true };
  }

  return { ...item, menuColumns: filteredColumns };
};

export const useMenuItemsWithPermissions = (): MainMenuItem[] => {
  const isDisableAi = useSelector(selectIsDisableAi);
  const defaultCreationEnv = useSelector(selectDefaultCreationEnvironment);
  const isNonSalesforceEnv = Boolean(
    defaultCreationEnv?.orgType && defaultCreationEnv.orgType !== 'salesforce',
  );

  const { orgMigrationDemo } = useFeatureToggle();

  const menuItemsWithPermissions = useMemo(() => {
    return mainMenuItems.map((item) => {
      if (item.route === appRoutes.agenticLayer.route) {
        return {
          ...item,
          isHidden: isDisableAi,
        };
      }

      if (isNonSalesforceEnv) {
        const isAllowedRoute = NON_SALESFORCE_ALLOWED_ROUTES.includes(item.route);
        const isAllowedMenuId = NON_SALESFORCE_ALLOWED_MENU_IDS.includes(item.route);

        if (!isAllowedRoute && !isAllowedMenuId) {
          return { ...item, isHidden: true };
        }

        if (isAllowedMenuId) {
          return filterMenuColumnsForNonSalesforce(item);
        }

        return item;
      }

      if (orgMigrationDemo && item.route === 'apps1') {
        return {
          ...item,
          menuColumns: item.menuColumns
            ? [
                {
                  ...item.menuColumns[0],
                  menuItems: [...item.menuColumns[0].menuItems, orgMigrationMenuItem],
                },
              ]
            : undefined,
        };
      }

      return item;
    });
  }, [isDisableAi, isNonSalesforceEnv, orgMigrationDemo]);

  return menuItemsWithPermissions;
};
